import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ExperienceLetterData {
  date: string;
  refNo?: string;
  employeeName: string;
  role: string;
  startDate: string;
  endDate: string;
  letterContent?: string;
  logoUrl?: string;
  signatureImageUrl?: string;
}

// Company Details
const COMPANY_DETAILS = {
  name: 'BOZ MEET CONNECT AND TRAVEL PVT LTD',
  address: 'Hustlehub Tech Park, 36/5, Somasundarapalya Main Rd, HSR Layout, Bengaluru, Karnataka 560102',
  gstin: '29AALCB5090K1Z5',
  phone: '9591592804',
  website: 'www.bozapp.in',
  logoUrl: 'https://img.bozapp.in/t/w_400/bdbcc6b6-122a-4655-a57e-3967e1e08aff.jpg'
};

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatDateWithOrdinal(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-IN', { month: 'long' });
    const year = date.getFullYear();
    
    // Add ordinal suffix
    const getOrdinal = (n: number) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    return `${getOrdinal(day)} ${month} ${year}`;
  } catch {
    return dateStr;
  }
}

export async function generateExperienceLetterPDF(data: ExperienceLetterData): Promise<void> {
  // Escape user input to prevent XSS
  const safeDate = escapeHtml(data.date || '___ / ___ / 2025');
  const safeRefNo = escapeHtml(data.refNo || '___________');
  const safeEmployeeName = escapeHtml(data.employeeName);
  const safeRole = escapeHtml(data.role);
  const safeStartDate = formatDateWithOrdinal(data.startDate);
  const safeEndDate = formatDateWithOrdinal(data.endDate);
  const safeLetterContent = data.letterContent 
    ? escapeHtml(data.letterContent).replace(/\n/g, '<br>')
    : `During his tenure, ${safeEmployeeName} discharged his duties with sincerity and professionalism. He was responsible for ${safeRole.toLowerCase()} functions and other related duties. His performance and conduct were satisfactory.<br><br>He has been relieved from his duties with effect from <span class="bold">${safeEndDate}</span>. We confirm that he has no dues pending with the company.<br><br>We wish him success in his future endeavors.`;
  
  // Load logo - use provided URL or default company logo
  const logoUrl = data.logoUrl || COMPANY_DETAILS.logoUrl;
  const logoImgTag = `<img src="${logoUrl}" alt="Company Logo" style="width:120px;height:auto;border-radius:6px;object-fit:contain;" />`;

  // Signature image
  const signatureImgTag = data.signatureImageUrl 
    ? `<img src="${data.signatureImageUrl}" alt="Signature" style="width:180px;height:auto;margin:0;border-radius:4px;object-fit:contain;" />`
    : '';

  // Create a temporary container for the experience letter HTML
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.width = '210mm'; // A4 width
  tempContainer.style.background = '#f3f4f6';
  tempContainer.style.padding = '24px';
  tempContainer.style.fontFamily = 'Inter, -apple-system, system-ui, Roboto, "Helvetica Neue", Arial';
  
  // Create style element
  const style = document.createElement('style');
  style.textContent = `
    .sheet{ 
      width:210mm; 
      min-height:297mm; 
      margin:20px auto; 
      background:#fff; 
      box-shadow:0 6px 18px rgba(12,12,12,0.08); 
      border-radius:8px; 
      padding:20mm 18mm; 
      display:flex; 
      flex-direction:column; 
    }
    header{ 
      display:flex; 
      align-items:center; 
      gap:18px; 
      border-bottom:1px solid #e6e6e6; 
      padding-bottom:18px; 
    }
    .logo{ 
      width:120px; 
      height:80px; 
      display:flex; 
      align-items:center; 
      justify-content:center; 
      border-radius:6px; 
    }
    .logo img {
      width:120px;
      height:auto;
      border-radius:6px;
      object-fit:contain;
    }
    .company{ flex:1; }
    .company h1{ margin:0; font-size:20px; letter-spacing:0.2px; color:#111; }
    .company p{ margin:4px 0 0 0; color:#666; font-size:13px; }
    .meta{ text-align:right; color:#666; font-size:13px; }
    main{ padding:18mm 0 12mm 0; line-height:1.7; flex:1; }
    h2{ text-align:center; margin:12px 0 24px 0; font-size:20px; letter-spacing:0.3px; color:#111; }
    .content p{ margin:14px 0; font-size:15px; color:#111; }
    .bold{ font-weight:600; }
    .signature{ margin-top:36px; display:flex; justify-content:space-between; align-items:center; }
    .sig-left{ color:#666; font-size:13px; }
    .sig-right{ text-align:center; }
    .sig-block{ border-top:1px solid #ccc; width:260px; padding-top:8px; }
    .signature img {
      width:180px;
      height:auto;
      margin:0;
      border-radius:4px;
      object-fit:contain;
    }
  `;
  document.head.appendChild(style);
  
  // Create the sheet content
  tempContainer.innerHTML = `
    <div class="sheet">
      <header>
        <div class="logo" style="background:none;padding:0;">
          ${logoImgTag}
        </div>
        <div class="company">
          <h1>${COMPANY_DETAILS.name}</h1>
          <p>${COMPANY_DETAILS.address}</p>
          <p>GSTIN: ${COMPANY_DETAILS.gstin} | Phone: ${COMPANY_DETAILS.phone} | ${COMPANY_DETAILS.website}</p>
        </div>
        <div class="meta">
          <div><strong>Date:</strong> <span>${safeDate}</span></div>
          <div style="margin-top:6px;"><strong>Ref No.:</strong> ${safeRefNo}</div>
        </div>
      </header>

      <main>
        <h2>EXPERIENCE-CUM-RELIEVING LETTER</h2>

        <div class="content">
          <p>To Whomsoever It May Concern,</p>

          <p>This is to certify that <span class="bold">Mr. ${safeEmployeeName}</span> was employed with <span class="bold">${COMPANY_DETAILS.name}</span> as a <span class="bold">${safeRole}</span> from <span class="bold">${safeStartDate}</span> to <span class="bold">${safeEndDate}</span>.</p>

          <p>${safeLetterContent}</p>

          <div class="signature" style="margin-top:140px;">
            <div class="sig-left">
              For <strong>${COMPANY_DETAILS.name}</strong>
              <br/>
              ${signatureImgTag ? signatureImgTag + '<div style="height:28px"></div>' : '<div style="height:64px"></div>'}
              <div class="sig-block">Authorized Signatory</div>
            </div>

            <div class="sig-right">
              <div style="height:64px"></div>
            </div>
          </div>
        </div>

      </main>
    </div>
  `;

  // Append to body temporarily
  document.body.appendChild(tempContainer);

  try {
    // Wait for fonts and images to load
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Get the sheet element
    const sheetElement = tempContainer.querySelector('.sheet') as HTMLElement;
    if (!sheetElement) {
      throw new Error('Could not find sheet element');
    }

    // Convert to canvas using html2canvas with high quality settings
    const canvas = await html2canvas(sheetElement, {
      scale: 2.5, // Higher scale for better quality (2.5x = 240 DPI)
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      imageTimeout: 15000, // Increase timeout for image loading
      width: sheetElement.scrollWidth || 794, // A4 width in pixels at 96 DPI
      height: sheetElement.scrollHeight || 1123, // A4 height in pixels at 96 DPI
    });

    // Ensure canvas is properly initialized
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Failed to create canvas or canvas has zero dimensions');
    }

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    // Calculate dimensions
    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Convert canvas to JPEG with high quality (0.95)
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    
    // Verify the data URL is valid
    if (!imgData || !imgData.startsWith('data:image/')) {
      throw new Error('Failed to convert canvas to image data');
    }

    // Add image to PDF
    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');

    // Download PDF
    const fileName = `Experience_Letter_${data.employeeName.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempContainer);
    // Remove the style element we added
    const addedStyle = document.head.querySelector('style:last-of-type');
    if (addedStyle && addedStyle.textContent?.includes('.sheet')) {
      document.head.removeChild(addedStyle);
    }
  }
}

